import { JsonPipe } from '@angular/common';
import { buildDeleteConfig } from '../../utils/delete-confirmation';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { Subscription } from 'rxjs';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { StoreInterfaceService } from '../../services/store-interface.service';
import { TestModeService } from '../../services/test-mode.service';
import { BusinessRule, BusinessRuleType, EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { BaseFormulaModel, Formula, FormulaParameters } from '../../types/formula.interface';
import { Unit } from '../../types/unit.interface';
import { CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesDialogFooterComponent } from '../ui/dialog-footer/dialog-footer.component';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';
import { EditorComponent } from '../ui/inputs/editor/editor.component';
import { EditorVariable } from '../ui/inputs/editor/editor.types';
import { ParameterMode } from '../ui/inputs/parameters-input/parameter-types';
import { ParametersComponent } from '../ui/inputs/parameters-input/parameters.component';

interface FormulaModelFormControls {
  name: FormControl<string>;
  comment: FormControl<string>;
  expression: FormControl<string>;
  parameters: FormControl<FormulaParameters>;
}

@Component({
  selector: 'app-formula-model-form',
  standalone: true,
  imports: [
    FormsModule,
    JsonPipe,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    EditorComponent,
    ParametersComponent,
    CesTextFieldComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    TranslocoModule,
  ],
  templateUrl: './formula-model-form.component.html',
})
export class FormulaModelFormComponent<T extends BaseFormulaModel> implements OnInit, AfterViewInit, OnDestroy {
  debugTrace: FormulaParameters = [];

  ParameterMode = ParameterMode;

  private readonly entityDefinitionSignal = signal<EntityDefinition<T> | undefined>(undefined);
  private formValueSubscription?: Subscription;

  private externalErrors: Record<string, Record<string, string>> = {};
  private initialValidationDone = false;

  @Input() set entityDefinition(value: EntityDefinition<T> | undefined) {
    this.entityDefinitionSignal.set(value);
  }

  get entityDefinition(): EntityDefinition<T> | undefined {
    return this.entityDefinitionSignal();
  }

  @Input() entity?: T;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;
  @Input() unitOptions: Unit[] = [];

  @Output() formSubmit = new EventEmitter<EntityFormValue<T>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<T>();

  form: FormGroup<FormulaModelFormControls> = this.buildEmptyForm();
  parametersControl: FormControl<FormulaParameters> = this.form.controls.parameters;
  editorVariables: EditorVariable[] = [];
  submitting = false;
  errorMessage = '';

  private readonly config = inject<DynamicDialogConfig<EntityDialogData<T>> | null>(DynamicDialogConfig, { optional: true });
  private readonly dialogRef = inject<DynamicDialogRef<FormulaModelFormComponent<T>> | null>(DynamicDialogRef, { optional: true });
  private readonly storeService = inject(StoreInterfaceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  protected readonly t = inject(TestModeService);
  private readonly translateService = inject(TranslocoService);

  private directDeleteCallback?: (entity: T) => void;
  private validationTypes: BusinessRuleType[] = [];

  private buildEmptyForm(): FormGroup<FormulaModelFormControls> {
    return new FormGroup<FormulaModelFormControls>({
      name: new FormControl<string>('', { nonNullable: true }),
      comment: new FormControl<string>('', { nonNullable: true }),
      expression: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      parameters: new FormControl<FormulaParameters>([], { nonNullable: true }),
    });
  }

  readonly parameterMode = computed<ParameterMode>(() => {
    const type = this.entityDefinitionSignal()?.entityType;
    switch (type) {
      case 'FormulaModel':
        return ParameterMode.FORMULA_MODEL;
      case 'PolynomialModel':
        return ParameterMode.POLYNOMIAL_MODEL;
      case 'Formula':
        return ParameterMode.FORMULA;
      case 'Polynomial':
        return ParameterMode.POLYNOMIAL;
      default:
        return ParameterMode.FORMULA_MODEL;
    }
  });

  ngOnInit(): void {
    const data = this.config?.data;
    if (data) {
      this.entityDefinition = data.entityDefinition;
      this.mode = data.mode === 'edit' ? 'edit' : 'create';
      if (data.entity !== undefined) this.entity = data.entity;
      if (data.parentId !== undefined) this.parentId = data.parentId;
      if (data.additionalData !== undefined) this.additionalData = data.additionalData;
      if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;

      const unitOptions = this.additionalData?.['unitOptions'];
      if (Array.isArray(unitOptions)) {
        this.unitOptions = unitOptions as Unit[];
      }
    }

    this.initForm();

    if (this.entity?.formula) {
      this.patchFormWithEntity(this.entity);
    }

    this.validationTypes = [this.mode === 'create' ? BusinessRuleType.CREATE_VALIDATION : BusinessRuleType.EDIT_VALIDATION];
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.validateBusinessRules();
      this.setupFormValueChanges();
      this.initialValidationDone = true;
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.formValueSubscription) {
      this.formValueSubscription.unsubscribe();
    }
  }

  private initForm(): void {
    this.form = this.buildEmptyForm();
    this.parametersControl = this.form.controls.parameters;

    this.parametersControl.valueChanges.subscribe(parameters => {
      if (parameters) {
        this.updateEditorVariables(parameters);
      }
    });
  }

  private setupFormValueChanges(): void {
    this.formValueSubscription = this.form.valueChanges.subscribe(() => {
      this.zone.run(() => {
        this.validateBusinessRules();
      });
    });
  }

  private validateBusinessRules(): void {
    const validationRules = this.entityDefinition?.validationRules;
    if (!validationRules || !this.storeService) {
      return;
    }

    const newExternalErrors: Record<string, Record<string, string>> = {};
    newExternalErrors['name'] = {};

    const formValue = this.form.getRawValue();
    const entityToValidate = {
      ...formValue,
      ...(this.entity ? { id: this.entity.id } : { id: undefined }),
    } as unknown as T;

    const rules: BusinessRule<T>[] = validationRules.filter(rule => this.validationTypes.includes(rule.type));

    for (const rule of rules) {
      try {
        if (!rule.rule(entityToValidate, this.storeService)) {
          newExternalErrors['name'] = {
            ...newExternalErrors['name'],
            businessRule: rule.errorMessage ?? '',
          };
          break;
        }
      } catch (error) {
        console.error(this.translateService.translate('common.errors.businessRuleValidationError'), error);
      }
    }

    this.externalErrors = newExternalErrors;

    if (this.initialValidationDone) {
      this.cdr.detectChanges();
    }
  }

  private patchFormWithEntity(entity: T): void {
    if (!entity.formula) return;

    const parameters = entity.formula.parameters ?? [];
    this.updateEditorVariables(parameters);

    this.form.patchValue({
      name: entity.name,
      comment: entity.formula.comment ?? '',
      expression: entity.formula.expression,
      parameters,
    });
  }

  private updateEditorVariables(parameters: FormulaParameters): void {
    this.editorVariables = parameters.map(param => ({
      name: param.symbol,
      value: param.name,
      description: param.name,
    }));
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field?.errors) return '';

    if (field.errors['required']) return this.translateService.translate('validation.required');
    const minLengthError = field.errors['minlength'] as { requiredLength: number } | undefined;
    if (minLengthError) {
      return this.translateService.translate('validation.minLength', { min: minLengthError.requiredLength });
    }
    const maxLengthError = field.errors['maxlength'] as { requiredLength: number } | undefined;
    if (maxLengthError) {
      return this.translateService.translate('validation.maxLength', { max: maxLengthError.requiredLength });
    }

    return this.translateService.translate('validation.pattern');
  }

  getExternalErrors(fieldName: string): Record<string, string> {
    return this.externalErrors[fieldName] ?? {};
  }

  isFormInvalid(): boolean {
    if (this.form.invalid || this.submitting || this.hasBusinessRuleErrors()) {
      return true;
    }

    return !this.form.controls.expression.value.trim();
  }

  private hasBusinessRuleErrors(): boolean {
    return Object.values(this.externalErrors).some(fieldErrors => Object.keys(fieldErrors).length > 0);
  }

  onParametersChange(parameters: FormulaParameters): void {
    this.debugTrace = parameters;
    this.updateEditorVariables(parameters);
  }

  onExpressionChange(_expression: string): void {}

  onSubmit(): void {
    if (this.form.invalid || this.hasBusinessRuleErrors() || this.submitting) {
      this.form.markAllAsTouched();

      const businessRule = this.externalErrors['name']?.['businessRule'];
      if (businessRule) {
        this.errorMessage = businessRule;
      }

      return;
    }

    this.submitting = true;
    this.errorMessage = '';
    const formValue = this.form.getRawValue();

    const formula: Formula = {
      ...(this.entity?.formula ?? ({} as Formula)),
      name: formValue.name,
      comment: formValue.comment,
      expression: formValue.expression,
      parameters: formValue.parameters,
    };
    const formData = {
      ...(this.entity ?? ({} as T)),
      name: formValue.name,
      formula,
    } as T;

    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData as EntityFormValue<T>);
    }

    this.submitting = false;
  }

  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  getDeleteConfirmationConfig() {
    return buildDeleteConfig(
      'entities.formulaModel.messages.deleteConfirm',
      (this.entity as { name?: string } | undefined)?.name ?? '',
    );
  }

  onDelete(): void {
    if (!this.entity) return;

    if (this.directDeleteCallback) {
      this.directDeleteCallback(this.entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<T> = { __action: 'delete', entity: this.entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(this.entity);
    }
  }
}
